"use client";

import React from "react";
import { ComponentConfig } from "@puckeditor/core";
import Link from "next/link";
import { businessData } from "@/data/business";
import { productSlug } from "@/data/product-catalog";
import { ImagePicker } from "@/components/ImagePicker";
import { resolveMediaUrl } from "@/lib/resolve-media-url";

export interface CategoryDetailProps {
  categoryId?: string;
  title?: string;
  tagline?: string;
  body?: string;
  image?: string;
  products?: Array<{ item: string }>;
  primaryCtaText?: string;
  primaryCtaLink?: string;
  secondaryCtaText?: string;
  secondaryCtaLink?: string;
  productsHeading?: string;
  productsIntro?: string;
}

export const CategoryDetailBlock: ComponentConfig<CategoryDetailProps> = {
  label: "Category Detail View",
  fields: {
    categoryId: {
      type: "select",
      label: "Product Category",
      options: businessData.categories.map((category) => ({ label: category.title, value: category.id })),
    },
    title: { type: "text", label: "Category Title" },
    tagline: { type: "text", label: "Tagline" },
    body: { type: "textarea", label: "Detailed Description" },
    image: { type: "custom", label: "Hero Image", render: ({ value, onChange }: any) => <ImagePicker value={value} onChange={onChange} /> },
    primaryCtaText: { type: "text", label: "Primary Button Text" },
    primaryCtaLink: { type: "text", label: "Primary Button Link" },
    secondaryCtaText: { type: "text", label: "Secondary Button Text" },
    secondaryCtaLink: { type: "text", label: "Secondary Button Link" },
    productsHeading: { type: "text", label: "Product List Heading" },
    productsIntro: { type: "textarea", label: "Product List Introduction" },
    products: {
      type: "array",
      getItemSummary: (p: any) => p || "Product Item",
      arrayFields: {
        item: { type: "text", label: "Product Name" },
      },
      defaultItemProps: { item: "New private-label formulation" },
    },
  },
  defaultProps: {
    categoryId: "skin-care",
    title: "Advanced Skin Care",
    tagline: "Clinical serums, barrier repair creams, treatments & gentle cleansers.",
    body: "Develop private-label serums, cleansers, treatments, and barrier-care products against an approved brief covering active level, skin feel, fragrance, packaging, testing, and target market.",
    image: "/assets/categories/skin-care-v2.jpg",
    products: businessData.categoryProducts["skin-care"].map((item) => ({ item })),
    primaryCtaText: "Request Samples",
    primaryCtaLink: "/contact",
    secondaryCtaText: "View All Suites",
    secondaryCtaLink: "/product-range",
    productsHeading: "Tested Benchmark Formulations",
    productsIntro: "Ready-to-scale private-label starting formulations within this specialist category.",
  },
  render: ({ categoryId = "skin-care", title, tagline, body, image, products, primaryCtaText, primaryCtaLink, secondaryCtaText, secondaryCtaLink, productsHeading, productsIntro }) => {
    const catData = businessData.categories.find((c) => c.id === categoryId);
    const prodList: Array<string | { item: string }> = products && products.length > 0
      ? products
      : [...(businessData.categoryProducts[categoryId as keyof typeof businessData.categoryProducts] || [])];

    const displayTitle = title || catData?.title || "Advanced Skin Care";
    const displayTagline = tagline || catData?.tagline || "";
    const displayBody = body?.trim() || "";
    const showBody = displayBody && displayBody.toLocaleLowerCase() !== displayTagline.trim().toLocaleLowerCase();
    const displayImage = resolveMediaUrl(image || catData?.image, "/assets/categories/skin-care-v2.jpg");

    return (
      <div>
        <section className="sec cat-hero">
          <div className="container hero-grid">
            <div className="hero-copy">
              <h1>{displayTitle}</h1>
              <p className="lead">{displayTagline}</p>
              {showBody && <p className="category-detail-body">{displayBody}</p>}
              <div className="hero-cta category-hero-actions">
                <Link className="btn btn-primary" href={primaryCtaLink || "/contact"}>
                  {primaryCtaText || "Request Samples"}
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <path d="M5 12h14M12 5l7 7-7 7" />
                  </svg>
                </Link>
                <Link className="btn btn-ghost" href={secondaryCtaLink || "/product-range"}>
                  {secondaryCtaText || "View All Suites"}
                </Link>
              </div>
            </div>
            <div className="hero-media">
              <div className="hero-spec-card" style={{ padding: 0, overflow: "hidden", aspectRatio: "16/10" }}>
                <img src={displayImage} alt={displayTitle} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
              </div>
            </div>
          </div>
        </section>

      <section className="sec sec-alt">
        <div className="container">
          <div className="sec-head">
            <h2>{productsHeading || "Tested Benchmark Formulations"}</h2>
            <p className="sec-sub">{productsIntro || `Ready-to-scale private-label starting formulations within the ${displayTitle} category.`}</p>
          </div>
          <div className="category-product-list">
            {prodList.map((prod, idx) => {
              const productName = typeof prod === "string" ? prod : (prod as any).item || String(prod);
              return (
                <Link href={`/products/${productSlug(productName)}`} key={idx}>
                  <span>{productName}</span>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true"><path d="M5 12h14M13 6l6 6-6 6" /></svg>
                </Link>
              );
            })}
          </div>
        </div>
      </section>
    </div>
    );
  },
};
